import axios from "axios";

// Single source of truth for the backend URL. Read once at module load.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Shared axios instance — every request flows through this client so that
// changes to base URL, headers, timeouts, or interceptors apply globally.
export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// ---------- Error normalization ----------
// Convert axios errors into a small object every caller can rely on.
// `status` is 0 when the browser blocked the request (CORS, offline, DNS).
const normalizeError = (err, fallbackMsg) => {
  const status = err?.response?.status ?? 0;
  const data = err?.response?.data;
  const detail =
    (typeof data === "string" && data) ||
    data?.detail ||
    data?.message ||
    err?.message ||
    fallbackMsg ||
    "Request failed";
  const wrapped = new Error(detail);
  wrapped.status = status;
  wrapped.data = data;
  wrapped.original = err;
  return wrapped;
};

// ---------- Core helpers ----------
// `path` MUST be the route after `/api` (e.g. "/products", "/personas/young-families").
// We deliberately do NOT accept absolute URLs — keeps env-driven base URL enforced.
export const apiGet = async (path, config = {}) => {
  try {
    const { data } = await apiClient.get(path, config);
    return data;
  } catch (err) {
    throw normalizeError(err, `GET ${path} failed`);
  }
};

export const apiPost = async (path, body = {}, config = {}) => {
  try {
    const { data } = await apiClient.post(path, body, config);
    return data;
  } catch (err) {
    throw normalizeError(err, `POST ${path} failed`);
  }
};

// PDF / file download — returns a Blob, not parsed JSON.
export const apiPostBlob = async (path, body = {}, config = {}) => {
  try {
    const { data } = await apiClient.post(path, body, {
      ...config,
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw normalizeError(err, `POST ${path} failed`);
  }
};

// ---------- Convenience wrappers used by hooks/pages ----------
export const fetchProducts = () => apiGet("/products");
export const fetchAdvisors = () => apiGet("/advisors");
export const fetchLifeStages = () => apiGet("/life-stages");

// Tools (calculator) submissions
export const postTool = (slug, body) => apiPost(`/tools/${slug}`, body);
